/**
 * Administrator panel of the platform's AI provider profiles (ADR-0019).
 *
 * The panel is the only place a platform credential is entered, and it is deliberately one-way: a key travels up with a
 * create or an update and is never sent back down, so the form can only ever show the fingerprint the vault derived. That is
 * why an edit leaves the key field empty and means "keep the stored one" — the alternative, prefilling the field, would
 * require the server to publish a secret.
 *
 * Three rules of the lifecycle are rendered rather than hidden, because each one changes what an operator must do next:
 *
 * - a profile with no credential cannot be enabled (it would refuse every call it is given);
 * - changing the provider family invalidates the stored credential (ADR-0019 §D1), so the panel warns before the edit and
 *   reports it afterwards;
 * - deleting is soft, so the row stays for the pricing and audit references that point at it.
 */

import { Plus, Save, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { LtrInput } from '@/components/ui/input';
import { SelectControl } from '@/components/ui/react-select';
import {
  Alert,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui/surface';
import {
  useCreateProviderProfile,
  useDeleteProviderProfile,
  useProviderProfiles,
  useSetProviderProfileState,
  useUpdateProviderProfile,
} from '@/features/forge/hooks/useForge';
import { useAiProviders } from '@/features/settings/hooks/useSettings';
import { providerDisplayName } from '@/features/settings/lib/providerLabels';
import { useLocale } from '@/hooks/useLocale';
import { useProblemMessage } from '@/lib/errors';
import type { AiProviderKind, AiProviderOption, ProviderProfile } from '@/types/api';

/** One draft row, held as strings so a half-typed field is never coerced behind the user's back. */
interface ProfileDraft {
  displayName: string;
  description: string;
  providerKind: AiProviderKind;
  baseUrl: string;
  /** Free-text model identifier, or `''` when the profile should not name one. */
  modelId: string;
  isVisibleToUsers: boolean;
  /** Replacement key, or `''` to keep the stored credential. */
  apiKey: string;
}

/** The empty draft a create form starts from. */
function emptyDraft(kind: AiProviderKind): ProfileDraft {
  return {
    displayName: '',
    description: '',
    providerKind: kind,
    baseUrl: '',
    modelId: '',
    isVisibleToUsers: true,
    apiKey: '',
  };
}

/** Renders the profile list, the create form and the lifecycle actions. */
export function ProviderProfileAdmin() {
  const { t, i18n } = useTranslation();
  const messageFor = useProblemMessage();
  const profiles = useProviderProfiles(true, true);
  const providers = useAiProviders();
  const create = useCreateProviderProfile();
  const update = useUpdateProviderProfile();
  const setState = useSetProviderProfileState();
  const remove = useDeleteProviderProfile();

  const catalogue = providers.data ?? [];
  const [draft, setDraft] = useState<ProfileDraft>(() => emptyDraft('OpenAi'));
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ProfileDraft>(() => emptyDraft('OpenAi'));

  /** Names a provider family the way the settings picker does, falling back to the raw value. */
  function kindLabel(kind: AiProviderKind): string {
    const descriptor = catalogue.find((provider) => provider.kind === kind);
    return descriptor ? providerDisplayName(descriptor, i18n.language) : kind;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('providerProfiles.title')}</CardTitle>
        <CardDescription>{t('providerProfiles.description')}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        {profiles.isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner />
            {t('common.loading')}
          </div>
        ) : null}

        {profiles.isError ? (
          <Alert variant="destructive">{messageFor(profiles.error)}</Alert>
        ) : null}
        {create.isError ? <Alert variant="destructive">{messageFor(create.error)}</Alert> : null}
        {update.isError ? <Alert variant="destructive">{messageFor(update.error)}</Alert> : null}
        {setState.isError ? (
          <Alert variant="destructive">{messageFor(setState.error)}</Alert>
        ) : null}
        {remove.isError ? <Alert variant="destructive">{messageFor(remove.error)}</Alert> : null}

        <ProfileList
          profiles={profiles.data ?? []}
          catalogue={catalogue}
          kindLabel={kindLabel}
          editing={editing}
          editDraft={editDraft}
          onEdit={(profile) => {
            setEditing(profile.id);
            setEditDraft({
              displayName: profile.displayName,
              description: profile.description ?? '',
              providerKind: profile.providerKind,
              baseUrl: profile.baseUrl,
              modelId: profile.modelId ?? '',
              isVisibleToUsers: profile.isVisibleToUsers,
              apiKey: '',
            });
          }}
          onCancelEdit={() => setEditing(null)}
          onEditDraftChange={(patch) => setEditDraft((current) => ({ ...current, ...patch }))}
          onSaveEdit={(profileId) => {
            update.mutate(
              {
                profileId,
                payload: {
                  displayName: editDraft.displayName,
                  providerKind: editDraft.providerKind,
                  description:
                    editDraft.description.trim().length === 0 ? null : editDraft.description,
                  baseUrl: editDraft.baseUrl.trim(),
                  modelId: editDraft.modelId.trim().length === 0 ? null : editDraft.modelId.trim(),
                  isVisibleToUsers: editDraft.isVisibleToUsers,
                  // An empty field keeps the stored credential; a value rotates it.
                  apiKey: editDraft.apiKey.trim().length === 0 ? null : editDraft.apiKey,
                },
              },
              { onSuccess: () => setEditing(null) },
            );
          }}
          onToggle={(profile) =>
            setState.mutate({ profileId: profile.id, isActive: !profile.isActive })
          }
          onDelete={(profile) => remove.mutate(profile.id)}
          busy={update.isPending || setState.isPending || remove.isPending}
        />

        <ProfileCreateForm
          draft={draft}
          catalogue={catalogue}
          onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
          onSubmit={() => {
            create.mutate(
              {
                displayName: draft.displayName,
                providerKind: draft.providerKind,
                apiKey: draft.apiKey,
                description: draft.description.trim().length === 0 ? null : draft.description,
                baseUrl: draft.baseUrl.trim(),
                modelId: draft.modelId.trim().length === 0 ? null : draft.modelId.trim(),
                isVisibleToUsers: draft.isVisibleToUsers,
              },
              { onSuccess: () => setDraft(emptyDraft(draft.providerKind)) },
            );
          }}
          busy={create.isPending}
        />
      </CardContent>
    </Card>
  );
}

/** Props accepted by {@link ProfileList}. */
interface ProfileListProps {
  /** Profiles as the API returned them, deleted ones included. */
  profiles: ProviderProfile[];
  /** Provider families this build can drive. */
  catalogue: AiProviderOption[];
  /** Names a provider family for display. */
  kindLabel: (kind: AiProviderKind) => string;
  /** Identifier of the profile being edited, or `null`. */
  editing: string | null;
  /** Draft of the profile being edited. */
  editDraft: ProfileDraft;
  /** Starts editing a profile. */
  onEdit: (profile: ProviderProfile) => void;
  /** Abandons an edit. */
  onCancelEdit: () => void;
  /** Applies a field change to the edit draft. */
  onEditDraftChange: (patch: Partial<ProfileDraft>) => void;
  /** Saves the edit draft. */
  onSaveEdit: (profileId: string) => void;
  /** Enables or disables a profile. */
  onToggle: (profile: ProviderProfile) => void;
  /** Soft-deletes a profile. */
  onDelete: (profile: ProviderProfile) => void;
  /** Whether a mutation is in flight. */
  busy: boolean;
}

/** The list of profiles, with the inline edit form of the one being edited. */
function ProfileList({
  profiles,
  catalogue,
  kindLabel,
  editing,
  editDraft,
  onEdit,
  onCancelEdit,
  onEditDraftChange,
  onSaveEdit,
  onToggle,
  onDelete,
  busy,
}: ProfileListProps) {
  const { t } = useTranslation();

  if (profiles.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('providerProfiles.empty')}</p>;
  }

  return (
    <ul className="flex flex-col gap-3">
      {profiles.map((profile) => (
        <li key={profile.id} className="rounded-lg border border-border p-3">
          {editing === profile.id ? (
            <ProfileFields
              draft={editDraft}
              catalogue={catalogue}
              onChange={onEditDraftChange}
              secretHint={t('providerProfiles.keepSecretHint')}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{profile.displayName}</span>
              <Badge variant="outline">{kindLabel(profile.providerKind)}</Badge>

              {/* The model is shown beside the family, because "which model" is the question the family alone does not answer. */}
              {profile.modelId ? (
                <span className="text-xs text-muted-foreground" dir="ltr">
                  {profile.modelId}
                </span>
              ) : null}

              {profile.isDeleted ? (
                <Badge variant="secondary">{t('providerProfiles.deleted')}</Badge>
              ) : profile.isActive ? (
                <Badge variant="success">{t('providerProfiles.active')}</Badge>
              ) : (
                <Badge variant="outline">{t('providerProfiles.disabled')}</Badge>
              )}

              {profile.isVisibleToUsers ? null : (
                <Badge variant="secondary">{t('providerProfiles.hidden')}</Badge>
              )}

              {profile.hasSecret ? (
                <span className="text-xs text-muted-foreground" dir="ltr">
                  {profile.fingerprint}
                </span>
              ) : (
                <Badge variant="secondary">{t('providerProfiles.noSecret')}</Badge>
              )}
            </div>
          )}

          {editing === profile.id ? (
            <div className="mt-3 flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={busy}
                onClick={() => onSaveEdit(profile.id)}
              >
                <Save aria-hidden className="h-4 w-4" />
                {t('common.save')}
              </Button>

              <Button type="button" size="sm" variant="ghost" onClick={onCancelEdit}>
                {t('common.cancel')}
              </Button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => onEdit(profile)}>
                {t('common.edit')}
              </Button>

              {/* A deleted profile is terminal from here: it is kept for its references, not for reuse. */}
              {profile.isDeleted ? null : (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => onToggle(profile)}
                  >
                    {profile.isActive
                      ? t('providerProfiles.disable')
                      : t('providerProfiles.enable')}
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => onDelete(profile)}
                  >
                    <Trash2 aria-hidden className="h-4 w-4" />
                    {t('providerProfiles.delete')}
                  </Button>
                </>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Props accepted by {@link ProfileFields}. */
interface ProfileFieldsProps {
  /** Draft being edited. */
  draft: ProfileDraft;
  /** Provider families this build can drive, so a family with no adapter is not offered. */
  catalogue: AiProviderOption[];
  /** Applies a field change. */
  onChange: (patch: Partial<ProfileDraft>) => void;
  /** Hint shown under the secret field, which is never prefilled. */
  secretHint: string;
}

/** The shared field set of the create and edit forms. */
function ProfileFields({ draft, catalogue, onChange, secretHint }: ProfileFieldsProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();

  // Driven by the catalogue the API returns, so a provider added server-side appears here without a release. The row shows
  // the name the interface language gives the family, while the family's own name stays searchable: an operator who knows
  // the endpoint types `openai`, not the Persian label.
  const providerOptions = catalogue.map((provider) => ({
    value: provider.kind,
    label: providerDisplayName(provider, locale),
    searchText: provider.kind,
  }));

  const chosenProvider = catalogue.find((provider) => provider.kind === draft.providerKind);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs text-muted-foreground">{t('providerProfiles.name')}</span>
        <input
          className="h-9 rounded-md border border-border bg-transparent px-3"
          value={draft.displayName}
          onChange={(event) => onChange({ displayName: event.target.value })}
        />
      </label>

      <div className="flex flex-col gap-1 text-sm">
        <span id="provider-profile-family-label" className="text-xs text-muted-foreground">
          {t('providerProfiles.provider')}
        </span>
        <SelectControl
          inputId="provider-profile-family"
          labelId="provider-profile-family-label"
          options={providerOptions}
          value={chosenProvider?.kind ?? null}
          onChange={(value) => {
            const next = catalogue.find((provider) => provider.kind === value);

            if (next !== undefined) {
              onChange({ providerKind: next.kind as AiProviderKind });
            }
          }}
        />
      </div>

      <label className="flex flex-col gap-1 text-sm sm:col-span-2">
        <span className="text-xs text-muted-foreground">{t('providerProfiles.baseUrl')}</span>
        {/* A base URL is always Latin, so the field opts into `dir="ltr"`. */}
        <LtrInput
          placeholder="https://api.openai.com/v1"
          value={draft.baseUrl}
          onChange={(event) => onChange({ baseUrl: event.target.value })}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm sm:col-span-2">
        <span className="text-xs text-muted-foreground">{t('providerProfiles.model')}</span>
        {/* Free text on purpose: a custom gateway serves model names this build cannot enumerate (amended ADR-0019 §D2). */}
        <LtrInput
          placeholder="gpt-4o-mini"
          value={draft.modelId}
          onChange={(event) => onChange({ modelId: event.target.value })}
        />
        <span className="text-xs text-muted-foreground">{t('providerProfiles.modelHint')}</span>
      </label>

      <label className="flex flex-col gap-1 text-sm sm:col-span-2">
        <span className="text-xs text-muted-foreground">{t('providerProfiles.apiKey')}</span>
        <LtrInput
          type="password"
          autoComplete="off"
          value={draft.apiKey}
          onChange={(event) => onChange({ apiKey: event.target.value })}
        />
        <span className="text-xs text-muted-foreground">{secretHint}</span>
      </label>

      <label className="flex items-center gap-2 text-sm sm:col-span-2">
        <input
          type="checkbox"
          checked={draft.isVisibleToUsers}
          onChange={(event) => onChange({ isVisibleToUsers: event.target.checked })}
        />
        {t('providerProfiles.visibleToUsers')}
      </label>
    </div>
  );
}

/** Props accepted by {@link ProfileCreateForm}. */
interface ProfileCreateFormProps {
  /** Draft of the new profile. */
  draft: ProfileDraft;
  /** Provider catalogue, so a family that this build cannot drive is not offered. */
  catalogue: AiProviderOption[];
  /** Applies a field change. */
  onChange: (patch: Partial<ProfileDraft>) => void;
  /** Submits the draft. */
  onSubmit: () => void;
  /** Whether the create request is in flight. */
  busy: boolean;
}

/** The create form, which is the only path that requires a key. */
function ProfileCreateForm({ draft, catalogue, onChange, onSubmit, busy }: ProfileCreateFormProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-dashed border-border p-3">
      <p className="mb-3 flex items-center gap-2 text-sm font-medium">
        <Plus aria-hidden className="h-4 w-4" />
        {t('providerProfiles.createTitle')}
      </p>

      <ProfileFields
        draft={draft}
        catalogue={catalogue}
        onChange={onChange}
        secretHint={t('providerProfiles.createSecretHint')}
      />

      <div className="mt-3">
        <Button
          type="button"
          size="sm"
          disabled={
            busy ||
            draft.displayName.trim().length === 0 ||
            draft.baseUrl.trim().length === 0 ||
            draft.apiKey.trim().length === 0
          }
          onClick={onSubmit}
        >
          <Plus aria-hidden className="h-4 w-4" />
          {t('providerProfiles.create')}
        </Button>
      </div>
    </div>
  );
}
